import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState, useEffect, useRef } from 'react';
import { ArrowDownRight, ArrowUpLeft, Check, ChevronDown, Globe, GripHorizontal, Headphones, Loader2, Maximize2, Mic, MicOff, Minimize2, Minus, PhoneOff, RotateCcw, Settings, X, MessageSquare, Captions } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { useRealtimeVoiceAssistant } from '@/hooks/useRealtimeVoiceAssistant';
import { startVoiceCall, endVoiceCall, fetchTutorCallInfo } from '@/api/chat';
import { cn } from '@/lib/utils';
import { useUserProfile } from '@/context/UserProfileContext';
import { usePreferences } from '@/context/PreferencesContext';
import { LANGUAGES } from './LanguageSelectionModal';
import { toast } from '@/hooks/use-toast';

const labelForState = (state, studentName = 'You', tutorName = 'Tutor') => {
  if (state === 'connecting') return 'Connecting...';
  if (state === 'reconnecting') return 'Reconnecting to tutor...';
  if (state === 'consulting') return 'Checking chapter notes...';
  if (state === 'thinking') return 'Connected';
  if (state === 'speaking') return `${tutorName} speaking`;
  if (state === 'listening') return `${studentName} speaking`;
  if (state === 'live') return 'Connected';
  if (state === 'error') return 'Connection issue';
  return 'Ready';
};

const formatTime = (totalSeconds) => {
  if (!totalSeconds) return '0s';
  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  }
  return `${Math.round(totalSeconds / 60)} min`;
};

const renderToolbarStatus = (state, isDark) => {
  let dotOrIcon = null;
  let text = 'Ready';
  let textColor = isDark ? 'text-white' : 'text-slate-900';

  if (state === 'speaking') {
    text = 'Speaking...';
    textColor = isDark ? 'text-blue-300' : 'text-blue-700';
    dotOrIcon = (
      <span className="relative flex h-2 w-2 shrink-0">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-500" />
      </span>
    );
  } else if (state === 'listening') {
    text = 'Listening...';
    textColor = isDark ? 'text-amber-300' : 'text-amber-700';
    dotOrIcon = (
      <span className="relative flex h-2 w-2 shrink-0">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-500" />
      </span>
    );
  } else if (state === 'thinking' || state === 'consulting') {
    text = 'Thinking...';
    textColor = isDark ? 'text-blue-300' : 'text-blue-700';
    dotOrIcon = (
      <Loader2 className="h-2.5 w-2.5 shrink-0 animate-spin text-blue-400" />
    );
  } else if (state === 'live' || state === 'connected') {
    text = 'Connected';
    textColor = isDark ? 'text-emerald-300' : 'text-emerald-700';
    dotOrIcon = (
      <span className="h-2 w-2 shrink-0 rounded-full bg-emerald-500" />
    );
  } else if (state === 'connecting' || state === 'reconnecting') {
    text = 'Connecting...';
    textColor = isDark ? 'text-amber-300' : 'text-amber-700';
    dotOrIcon = (
      <span className="h-2 w-2 shrink-0 rounded-full bg-amber-400 animate-pulse" />
    );
  } else if (state === 'error') {
    text = 'Connection issue';
    textColor = isDark ? 'text-red-400' : 'text-red-600';
    dotOrIcon = (
      <span className="h-2 w-2 shrink-0 rounded-full bg-red-500" />
    );
  } else {
    dotOrIcon = (
      <span className="h-2 w-2 shrink-0 rounded-full bg-slate-400" />
    );
  }

  return (
    <div className={cn("flex min-w-0 items-center gap-1.5 text-xs font-bold leading-tight", textColor)} data-testid="card-teach-toolbar-status">
      {dotOrIcon}
      <span className="truncate">{text}</span>
    </div>
  );
};

const CARD_TEACH_BOX_MARGIN = 12;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export const getCardTeachBoxSize = (mode = 'full') => {
  if (typeof window === 'undefined') {
    if (mode === 'bubble') return { width: 48, height: 48 };
    if (mode === 'minimized') return { width: 280, height: 64 };
    if (mode === 'settings') return { width: 380, height: 500 };
    return { width: 320, height: 360 };
  }

  const viewportWidth = window.visualViewport?.width || window.innerWidth || 360;
  const viewportHeight = window.visualViewport?.height || window.innerHeight || 640;
  if (mode === 'bubble') {
    return { width: 48, height: 48 };
  }
  if (mode === 'minimized') {
    return {
      width: Math.max(260, Math.min(340, viewportWidth - (CARD_TEACH_BOX_MARGIN * 2))),
      height: 64,
    };
  }
  if (mode === 'settings') {
    return {
      width: Math.max(280, Math.min(380, viewportWidth - (CARD_TEACH_BOX_MARGIN * 2))),
      height: Math.max(300, Math.min(520, viewportHeight - (CARD_TEACH_BOX_MARGIN * 2))),
    };
  }
  if (mode === 'visual') {
    return {
      width: Math.max(300, Math.min(440, viewportWidth - (CARD_TEACH_BOX_MARGIN * 2))),
      height: Math.max(380, Math.min(480, viewportHeight - 80)),
    };
  }

  return {
    width: Math.max(280, Math.min(320, viewportWidth - (CARD_TEACH_BOX_MARGIN * 2))),
    height: Math.max(300, Math.min(360, viewportHeight - 112)),
  };
};

export const getInitialCardTeachBoxPosition = (mode = 'full') => {
  if (typeof window === 'undefined') {
    return { x: CARD_TEACH_BOX_MARGIN, y: 80 };
  }

  const viewportWidth = window.visualViewport?.width || window.innerWidth || 360;
  const viewportHeight = window.visualViewport?.height || window.innerHeight || 640;
  const { width, height } = getCardTeachBoxSize(mode);
  const isMobile = viewportWidth < 768;

  if (mode === 'bubble') {
    return {
      x: Math.max(CARD_TEACH_BOX_MARGIN, viewportWidth - width - 16),
      y: clamp(viewportHeight - height - 84, 72, Math.max(72, viewportHeight - height - CARD_TEACH_BOX_MARGIN)),
    };
  }

  const initialX = isMobile
    ? Math.max(CARD_TEACH_BOX_MARGIN, Math.round((viewportWidth - width) / 2))
    : Math.max(CARD_TEACH_BOX_MARGIN, viewportWidth - width - 16);
  return {
    x: initialX,
    y: clamp(viewportHeight - height - 84, 72, Math.max(72, viewportHeight - height - CARD_TEACH_BOX_MARGIN)),
  };
};

// Web Audio API Ringtone Generator
const playDialTone = () => {
  if (typeof window === 'undefined') return null;
  try {
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const osc1 = audioCtx.createOscillator();
    const osc2 = audioCtx.createOscillator();
    const gainNode = audioCtx.createGain();

    osc1.frequency.setValueAtTime(440, audioCtx.currentTime);
    osc2.frequency.setValueAtTime(480, audioCtx.currentTime);

    osc1.connect(gainNode);
    osc2.connect(gainNode);
    gainNode.connect(audioCtx.destination);

    gainNode.gain.setValueAtTime(0.1, audioCtx.currentTime);

    osc1.start();
    osc2.start();

    // 2s ringing, 4s silent pattern
    let isRinging = true;
    const interval = setInterval(() => {
      try {
        if (isRinging) {
          gainNode.gain.setValueAtTime(0, audioCtx.currentTime);
        } else {
          gainNode.gain.setValueAtTime(0.1, audioCtx.currentTime);
        }
        isRinging = !isRinging;
      } catch (e) { }
    }, 2000);

    return {
      stop: () => {
        clearInterval(interval);
        try {
          osc1.stop();
          osc2.stop();
        } catch (e) { }
        try {
          if (audioCtx.state !== 'closed') {
            audioCtx.close().catch(() => { });
          }
        } catch (e) { }
      }
    };
  } catch (e) {
    console.error('Failed to start dial tone:', e);
    return null;
  }
};

const RealtimeVoiceAssistantOverlay = forwardRef(function RealtimeVoiceAssistantOverlay({
  open,
  onClose,
  context,
  onToolCall,
  onLessonEvent,
  tutor,
  minutesLeft,
}, ref) {
  const { profile } = useUserProfile();
  const { theme, updatePreferences } = usePreferences();
  const isDark = theme !== 'light';
  const studentFullName = profile?.full_name || 'You';

  const activeTutor = {
    full_name: tutor?.full_name || 'AI Tutor',
    subject_name: tutor?.subject_name || 'Budlee',
    avatar_url: tutor?.avatar_url || null,
    description: tutor?.description || 'Your voice learning assistant.',
    id: tutor?.id || null
  };

  const stableContext = useMemo(() => ({
    ...(context || {}),
    tutor_id: activeTutor.id || '',
  }), [context, activeTutor.id]);

  const isCardTeachMode = context?.voiceMode === 'card_teach' || context?.voice_mode === 'card_teach';
  const lessonManifest = context?.lessonManifest || context?.lesson_manifest || {};
  const visualCards = Array.isArray(lessonManifest?.cards) ? lessonManifest.cards : [];
  const isVisualLessonMode = visualCards.length > 0;
  const initialVisualIndex = Math.max(0, visualCards.findIndex((card) => card?.id === lessonManifest?.active_card_id));
  const [activeVisualIndex, setActiveVisualIndex] = useState(initialVisualIndex);
  const [visualHighlight, setVisualHighlight] = useState('');
  const lastAgentFocusedCardIdRef = useRef(null);

  const handleLessonEvent = useCallback((event) => {
    if (!event || event.action !== 'focus_card') return;
    if (event.card_id) {
      lastAgentFocusedCardIdRef.current = event.card_id;
    }
    const eventIndex = Number(event.card_index);
    const matchedIndex = visualCards.findIndex((card) => card?.id === event.card_id);
    const nextIndex = matchedIndex >= 0
      ? matchedIndex
      : (Number.isFinite(eventIndex) ? Math.max(0, Math.min(visualCards.length - 1, eventIndex)) : 0);
    setActiveVisualIndex(nextIndex);
    setVisualHighlight(String(event.highlight_label || '').trim());
    onLessonEvent?.(event);
  }, [onLessonEvent, visualCards]);

  const realtime = useRealtimeVoiceAssistant({
    context: stableContext,
    onToolCall,
    onLessonEvent: handleLessonEvent,
  });
  const [durationSeconds, setDurationSeconds] = useState(0);
  const [isTranscriptExpanded, setIsTranscriptExpanded] = useState(false);
  const [initialMinutesLeft, setInitialMinutesLeft] = useState(null);
  const [cardTeachBoxPosition, setCardTeachBoxPosition] = useState(null);
  const [bubblePosition, setBubblePosition] = useState(null);
  const [widgetCustomSize, setWidgetCustomSize] = useState(null);
  const [isCardTeachBubble, setIsCardTeachBubble] = useState(false);
  const [showBubbleShortcuts, setShowBubbleShortcuts] = useState(false);
  const cardTeachResizeRef = useRef(null);
  const [isCardTeachSettingsOpen, setIsCardTeachSettingsOpen] = useState(false);
  const [wasMinimizedBeforeSettings, setWasMinimizedBeforeSettings] = useState(false);
  const [teachSettingsInfo, setTeachSettingsInfo] = useState(null);
  const [teachSettingsLoading, setTeachSettingsLoading] = useState(false);
  const [teachSettingsSaving, setTeachSettingsSaving] = useState(false);
  const [teachSettingsError, setTeachSettingsError] = useState(null);
  const [teachSettingsNotice, setTeachSettingsNotice] = useState(null);
  const [selectedTeachLanguages, setSelectedTeachLanguages] = useState([]);

  useEffect(() => {
    setActiveVisualIndex(initialVisualIndex);
    setVisualHighlight('');
  }, [initialVisualIndex, lessonManifest?.active_card_id]);

  const activeCardId = lessonManifest?.active_card_id || context?.cardId || context?.card_id || '';
  const currentCardTitle = context?.cardTitle || context?.card_title || '';
  const currentCardContext = context?.cardContext || context?.card_context || '';

  // Synchronize card navigation to the backend agent when student navigates between cards
  useEffect(() => {
    if (!realtime.isOpen || !activeCardId) return;
    const isFromAgent = lastAgentFocusedCardIdRef.current === activeCardId;
    realtime.publishData({
      type: 'voice_lesson_navigation',
      action: 'navigate_card',
      source: isFromAgent ? 'agent_focus' : 'student_manual',
      card_id: activeCardId,
      card_index: initialVisualIndex,
      card_title: currentCardTitle,
      card_context: currentCardContext,
      lesson_manifest: lessonManifest,
    }, 'budlee.voice.lesson');
  }, [realtime.isOpen, activeCardId, currentCardTitle, currentCardContext, initialVisualIndex]);

  const subjectNameKey = context?.subjectName || context?.subject_name || '';
  const activeTutorId = activeTutor.id || context?.tutorId || context?.tutor_id || null;
  const [isFullScreen, setIsFullScreen] = useState(false);
  const isCompactMode = !isFullScreen;
  const isFloating = isCompactMode || isCardTeachBubble || isCardTeachSettingsOpen;
  const cardTeachSurfaceMode = isCardTeachSettingsOpen
    ? 'settings'
    : (isCardTeachBubble
      ? 'bubble'
      : (isCompactMode && visualCards.length ? 'visual' : (isCompactMode ? 'compact' : 'full')));

  // Fetch initial remaining minutes when call overlay opens
  useEffect(() => {
    if (open && (activeTutorId || subjectNameKey)) {
      fetchTutorCallInfo(activeTutorId || null, subjectNameKey)
        .then((data) => {
          if (data && data.minutes_left !== undefined) {
            setInitialMinutesLeft(data.minutes_left);
          }
        })
        .catch((err) => {
          console.error('Failed to fetch initial tutor call info:', err);
        });
    } else if (!open) {
      setInitialMinutesLeft(null);
    }
  }, [open, subjectNameKey, activeTutorId]);

  // Initialize transcript expanded state safely (avoid hydration mismatch)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsTranscriptExpanded(false);
    }
  }, []);

  const hasStartedCallLogRef = useRef(false);
  const prevIsOpenRef = useRef(false);
  const prevOpenRef = useRef(false);
  const ringtoneRef = useRef(null);
  const timerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const cardTeachDragRef = useRef(null);
  const hasMovedRef = useRef(false);

  // Auto-scroll transcript container
  useEffect(() => {
    if (isTranscriptExpanded && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [realtime.messages, isTranscriptExpanded]);

  useEffect(() => {
    if (!open || !isFloating || typeof window === 'undefined') return undefined;

    const clampPos = (pos, mode) => {
      if (!pos) return pos;
      const { width, height } = getCardTeachBoxSize(mode);
      const viewportWidth = window.visualViewport?.width || window.innerWidth || 360;
      const viewportHeight = window.visualViewport?.height || window.innerHeight || 640;
      return {
        x: clamp(pos.x, CARD_TEACH_BOX_MARGIN, Math.max(CARD_TEACH_BOX_MARGIN, viewportWidth - width - CARD_TEACH_BOX_MARGIN)),
        y: clamp(pos.y, CARD_TEACH_BOX_MARGIN, Math.max(CARD_TEACH_BOX_MARGIN, viewportHeight - height - CARD_TEACH_BOX_MARGIN)),
      };
    };

    const handleResize = () => {
      setCardTeachBoxPosition((current) => clampPos(current, cardTeachSurfaceMode === 'bubble' ? 'compact' : cardTeachSurfaceMode));
      setBubblePosition((current) => clampPos(current, 'bubble'));
    };

    window.addEventListener('resize', handleResize);
    window.visualViewport?.addEventListener?.('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.visualViewport?.removeEventListener?.('resize', handleResize);
    };
  }, [open, isFloating, cardTeachSurfaceMode]);

  useImperativeHandle(ref, () => ({
    open: realtime.start,
    close: realtime.stop,
  }), [realtime.start, realtime.stop]);

  // Handle Ringtone - disabled to eliminate artificial caller ringing tone
  useEffect(() => {
    if (ringtoneRef.current) {
      ringtoneRef.current.stop();
      ringtoneRef.current = null;
    }
  }, [open, realtime.state]);

  // Handle Call Timer and Call Log Start/End
  useEffect(() => {
    const isConnected = ['live', 'listening', 'speaking', 'consulting', 'thinking'].includes(realtime.state);

    if (isConnected) {
      // Start call duration timer if not running
      if (!timerRef.current) {
        timerRef.current = setInterval(() => {
          setDurationSeconds((prev) => prev + 1);
        }, 1000);
      }

      // Log start of call in DB
      if (!hasStartedCallLogRef.current && activeTutor.id && realtime.roomName) {
        hasStartedCallLogRef.current = true;
        startVoiceCall(activeTutor.id, realtime.roomName).catch((err) => {
          console.error('Failed to log voice call start:', err);
        });
      }
    } else {
      // Clear timer if disconnected
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }

    return () => {
      // Cleanup
    };
  }, [realtime.state, activeTutor.id, realtime.roomName]);

  // Handle Open/Close state synching
  useEffect(() => {
    if (open && !prevOpenRef.current && !realtime.isOpen) {
      setDurationSeconds(0);
      setIsTranscriptExpanded(false);
      setCardTeachBoxPosition(null);
      setBubblePosition(null);
      setWidgetCustomSize(null);
      setIsCardTeachBubble(false);
      setShowBubbleShortcuts(false);
      setIsCardTeachSettingsOpen(false);
      setWasMinimizedBeforeSettings(false);
      setIsFullScreen(false);
      hasStartedCallLogRef.current = false;
      prevIsOpenRef.current = false;
      realtime.start();
    }
    if (!open && realtime.isOpen) {
      realtime.stop();
    }
    prevOpenRef.current = open;
  }, [open, realtime, isCardTeachMode]);

  const close = async () => {
    let durationMinutes = 0;
    const durationSecs = durationSeconds;

    // 1. Log call end in DB
    if (realtime.roomName) {
      try {
        const endRes = await endVoiceCall(realtime.roomName);
        if (endRes && endRes.duration_minutes !== undefined) {
          durationMinutes = endRes.duration_minutes;
        }
      } catch (err) {
        console.error('Failed to log voice call end:', err);
      }
    }

    // 2. Fetch updated tutor call info for remaining minutes
    let minutesLeft = 0;
    try {
      const callInfo = await fetchTutorCallInfo(activeTutor.id || null, context?.subjectName || context?.subject_name);
      if (callInfo && callInfo.minutes_left !== undefined) {
        minutesLeft = callInfo.minutes_left;
      }
    } catch (err) {
      console.error('Failed to fetch updated call info:', err);
    }

    // 3. Compile summary data
    const summary = {
      tutor: activeTutor,
      durationSeconds: durationSecs,
      minutesUsed: durationMinutes || parseFloat((durationSecs / 60).toFixed(2)),
      minutesLeft: minutesLeft,
      chapterName: context?.chapter_name || context?.chapterName || '',
      questions: realtime.messages
        .filter(m => m.sender === 'user')
        .map(m => m.text)
        .filter(Boolean)
    };

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (ringtoneRef.current) {
      ringtoneRef.current.stop();
      ringtoneRef.current = null;
    }

    realtime.stop();
    onClose?.(summary);
  };
  // Auto-close if call gets disconnected after starting (e.g. server-side room deletion on quota exhaustion)
  useEffect(() => {
    if (prevIsOpenRef.current && !realtime.isOpen && open) {
      close();
    }
    prevIsOpenRef.current = realtime.isOpen;
  }, [realtime.isOpen, open]);
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (ringtoneRef.current) {
        ringtoneRef.current.stop();
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Dismiss bubble shortcuts pop-up on outside click
  useEffect(() => {
    if (!showBubbleShortcuts) return;
    const handleOutsideClick = (e) => {
      if (
        !e.target.closest('[data-testid="card-teach-floating-bubble"]') &&
        !e.target.closest('[data-testid="card-teach-bubble-shortcuts"]')
      ) {
        setShowBubbleShortcuts(false);
      }
    };
    window.addEventListener('pointerdown', handleOutsideClick);
    return () => window.removeEventListener('pointerdown', handleOutsideClick);
  }, [showBubbleShortcuts]);

  if (!open) return null;

  const showRipples = ['live', 'listening', 'speaking', 'consulting', 'thinking'].includes(realtime.state);
  const isSpeaking = realtime.state === 'speaking';
  const isUserSpeaking = realtime.state === 'listening';
  const isThinking = realtime.state === 'thinking' || realtime.state === 'consulting';
  const isConnecting = realtime.state === 'connecting' || realtime.state === 'reconnecting';
  const cardTitle = context?.cardTitle || context?.card_title || '';
  const activeVisualCard = visualCards[activeVisualIndex] || visualCards[0] || null;
  const activeVisualImage = activeVisualCard?.image_urls?.[0] || activeVisualCard?.imageUrls?.[0] || '';
  const hasActiveVisual = Boolean(isVisualLessonMode && activeVisualCard && !isCardTeachMode);
  const overlayChromeClass = isFloating
    ? "fixed z-[240] flex flex-col overflow-hidden rounded-2xl border shadow-2xl backdrop-blur-xl"
    : "fixed inset-x-0 z-[240] flex flex-col items-center justify-center overflow-hidden top-[calc(3.5rem+env(safe-area-inset-top,0px))] bottom-[calc(4rem+env(safe-area-inset-bottom,0px))] lg:inset-0";
  const overlayToneClass = isDark
    ? (isFloating ? "border-slate-700/80 bg-slate-950/95 text-white" : "bg-slate-950 text-white")
    : (isFloating ? "border-slate-200 bg-white/95 text-slate-900" : "bg-slate-50 text-slate-900");

  const effectiveMinutesLeft = initialMinutesLeft !== null ? initialMinutesLeft : (minutesLeft || 0);
  const initialSecondsLeft = effectiveMinutesLeft * 60;
  const remainingSecondsLeft = showRipples && initialMinutesLeft !== null
    ? Math.max(0, Math.floor(initialSecondsLeft - durationSeconds))
    : Math.floor(initialSecondsLeft);
  const baseBoxSize = isFloating ? getCardTeachBoxSize(cardTeachSurfaceMode) : null;
  const cardTeachBoxSize = isFloating
    ? (cardTeachSurfaceMode === 'bubble' || cardTeachSurfaceMode === 'settings'
        ? baseBoxSize
        : (widgetCustomSize || baseBoxSize))
    : null;
  const cardTeachBoxFallbackPosition = isFloating ? getInitialCardTeachBoxPosition(cardTeachSurfaceMode) : null;
  const cardTeachBoxStyle = isFloating && cardTeachBoxSize
    ? {
      width: `${cardTeachBoxSize.width}px`,
      height: `${cardTeachBoxSize.height}px`,
      left: `${(cardTeachBoxPosition || cardTeachBoxFallbackPosition).x}px`,
      top: `${(cardTeachBoxPosition || cardTeachBoxFallbackPosition).y}px`,
    }
    : undefined;

  const startCardTeachBoxDrag = (event) => {
    if (!isFloating || (typeof event.button === 'number' && event.button !== 0)) return;
    if (cardTeachSurfaceMode !== 'bubble' && event.target.closest('button, a, input, textarea, select, [role="button"], [data-no-drag]')) {
      return;
    }
    setShowBubbleShortcuts(false);
    hasMovedRef.current = false;
    const isBubble = cardTeachSurfaceMode === 'bubble';
    const startPosition = isBubble
      ? (bubblePosition || getInitialCardTeachBoxPosition('bubble'))
      : (cardTeachBoxPosition || cardTeachBoxFallbackPosition || getInitialCardTeachBoxPosition(cardTeachSurfaceMode));
    cardTeachDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: startPosition.x,
      originY: startPosition.y,
      isBubble,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  };

  const moveCardTeachBox = (event) => {
    const drag = cardTeachDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId || !isFloating) return;

    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
      hasMovedRef.current = true;
    }

    event.preventDefault?.();
    const currentSize = drag.isBubble
      ? getCardTeachBoxSize('bubble')
      : (cardTeachBoxSize || getCardTeachBoxSize(cardTeachSurfaceMode));
    const viewportWidth = window.visualViewport?.width || window.innerWidth || 360;
    const viewportHeight = window.visualViewport?.height || window.innerHeight || 640;
    const nextPos = {
      x: clamp(
        drag.originX + dx,
        CARD_TEACH_BOX_MARGIN,
        Math.max(CARD_TEACH_BOX_MARGIN, viewportWidth - currentSize.width - CARD_TEACH_BOX_MARGIN),
      ),
      y: clamp(
        drag.originY + dy,
        CARD_TEACH_BOX_MARGIN,
        Math.max(CARD_TEACH_BOX_MARGIN, viewportHeight - currentSize.height - CARD_TEACH_BOX_MARGIN),
      ),
    };

    if (drag.isBubble) {
      setBubblePosition(nextPos);
    } else {
      setCardTeachBoxPosition(nextPos);
    }
  };

  const startCardTeachResize = (event) => {
    if (typeof event.button === 'number' && event.button !== 0) return;
    event.stopPropagation();
    event.preventDefault();
    const clientX = event.clientX ?? event.nativeEvent?.clientX ?? 0;
    const clientY = event.clientY ?? event.nativeEvent?.clientY ?? 0;
    const currentWidth = widgetCustomSize?.width || getCardTeachBoxSize(cardTeachSurfaceMode).width;
    const currentHeight = widgetCustomSize?.height || getCardTeachBoxSize(cardTeachSurfaceMode).height;
    cardTeachResizeRef.current = {
      pointerId: event.pointerId ?? 1,
      startX: clientX,
      startY: clientY,
      startWidth: currentWidth,
      startHeight: currentHeight,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const moveCardTeachResize = (event) => {
    const resize = cardTeachResizeRef.current;
    if (!resize || (event.pointerId !== undefined && resize.pointerId !== event.pointerId)) return;
    event.stopPropagation();
    event.preventDefault?.();

    const clientX = event.clientX ?? event.nativeEvent?.clientX ?? 0;
    const clientY = event.clientY ?? event.nativeEvent?.clientY ?? 0;
    const dx = clientX - resize.startX;
    const dy = clientY - resize.startY;
    const viewportWidth = window.visualViewport?.width || window.innerWidth || 360;
    const viewportHeight = window.visualViewport?.height || window.innerHeight || 640;

    const minW = 280;
    const minH = 320;
    const maxW = Math.max(minW, viewportWidth - (CARD_TEACH_BOX_MARGIN * 2));
    const maxH = Math.max(minH, viewportHeight - (CARD_TEACH_BOX_MARGIN * 2));

    const newWidth = clamp(resize.startWidth + dx, minW, maxW);
    const newHeight = clamp(resize.startHeight + dy, minH, maxH);

    setWidgetCustomSize({
      width: Math.round(newWidth),
      height: Math.round(newHeight),
    });
  };

  const endCardTeachResize = (event) => {
    if (cardTeachResizeRef.current?.pointerId === event.pointerId) {
      cardTeachResizeRef.current = null;
      try {
        event.currentTarget.releasePointerCapture?.(event.pointerId);
      } catch (e) {}
    }
  };

  const expandFromBubble = () => {
    setIsCardTeachBubble(false);
    setShowBubbleShortcuts(false);
  };

  const collapseToBubble = () => {
    setIsCardTeachBubble(true);
    setShowBubbleShortcuts(false);
    setBubblePosition((prev) => prev || getInitialCardTeachBoxPosition('bubble'));
  };

  const minimizeCardTeachBox = () => {
    setIsTranscriptExpanded(false);
    setIsCardTeachSettingsOpen(false);
    setWasMinimizedBeforeSettings(false);
    setIsFullScreen(false);
    setShowBubbleShortcuts(false);
    collapseToBubble();
  };

  const restoreCardTeachBox = () => {
    expandFromBubble();
  };

  const loadTeachSettings = async () => {
    setTeachSettingsLoading(true);
    setTeachSettingsError(null);
    try {
      const data = await fetchTutorCallInfo(activeTutor.id || null, subjectNameKey || null, {
        subjectId: context?.subjectId || context?.subject_id,
        chapterId: context?.chapterId || context?.chapter_id,
        allowFallbackTutor: context?.allowFallbackTutor || context?.allow_fallback_tutor,
      });
      const preferred = Array.isArray(data?.preferred_languages) ? data.preferred_languages : [];
      setTeachSettingsInfo(data);
      setSelectedTeachLanguages(preferred.slice(0, 3));
    } catch (error) {
      console.error('Failed to load tutor settings:', error);
      setTeachSettingsError(error?.response?.data?.error || 'Could not load tutor settings.');
    } finally {
      setTeachSettingsLoading(false);
    }
  };

  const openTeachSettings = (fromMinimized = false) => {
    setWasMinimizedBeforeSettings(Boolean(fromMinimized || isCardTeachBubble));
    setIsTranscriptExpanded(false);
    setIsCardTeachBubble(false);
    setShowBubbleShortcuts(false);
    setTeachSettingsNotice(null);
    setIsCardTeachSettingsOpen(true);
    loadTeachSettings();
  };

  const closeTeachSettings = () => {
    setIsCardTeachSettingsOpen(false);
    if (wasMinimizedBeforeSettings) {
      setCardTeachBoxPosition(null);
      setIsCardTeachBubble(true);
      setShowBubbleShortcuts(false);
    }
    setWasMinimizedBeforeSettings(false);
  };

  const toggleTeachLanguage = (code) => {
    setSelectedTeachLanguages((current) => {
      if (current.includes(code)) {
        setTeachSettingsNotice(null);
        return current.filter((item) => item !== code);
      }
      if (current.length >= 3) {
        setTeachSettingsNotice('Selection limit reached. You can select up to 3 languages.');
        return current;
      }
      setTeachSettingsNotice(null);
      return [...current, code];
    });
  };

  const saveTeachLanguages = async () => {
    setTeachSettingsSaving(true);
    try {
      await updatePreferences({ preferred_languages: selectedTeachLanguages });
      toast({
        title: isCardTeachMode ? 'Teach me settings updated' : 'Tutor settings updated',
        description: 'Your tutor language preferences have been saved.',
      });
      await loadTeachSettings();
      closeTeachSettings();
    } catch (error) {
      console.error('Failed to save tutor settings:', error);
      toast({
        title: 'Could not save settings',
        description: 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setTeachSettingsSaving(false);
    }
  };

  const endCardTeachBoxDrag = (event) => {
    const drag = cardTeachDragRef.current;
    if (drag?.pointerId === event.pointerId) {
      const isBubble = drag.isBubble || cardTeachSurfaceMode === 'bubble';
      cardTeachDragRef.current = null;
      try {
        event.currentTarget.releasePointerCapture?.(event.pointerId);
      } catch (e) {}

      if (isBubble) {
        const viewportWidth = window.visualViewport?.width || window.innerWidth || 360;
        const { width: bubbleWidth } = getCardTeachBoxSize('bubble');
        setBubblePosition((pos) => {
          const currentPos = pos || getInitialCardTeachBoxPosition('bubble');
          const midX = viewportWidth / 2;
          const isRight = (currentPos.x + bubbleWidth / 2) >= midX;
          const snappedX = isRight
            ? Math.max(CARD_TEACH_BOX_MARGIN, viewportWidth - bubbleWidth - 16)
            : 16;
          return {
            x: snappedX,
            y: currentPos.y,
          };
        });
      }
    }
  };

  if (isCardTeachSettingsOpen) {
    return (
      <div
        className={cn(overlayChromeClass, overlayToneClass)}
        style={cardTeachBoxStyle}
        data-testid={isCardTeachMode ? "card-teach-settings-panel" : "call-tutor-settings-panel"}
      >
        <div className="relative z-10 flex h-full min-h-0 flex-col">
          <div
            className={cn(
              "flex shrink-0 items-center justify-between gap-3 border-b px-3.5 py-3",
              isDark ? "border-slate-800" : "border-slate-200"
            )}
          >
            <div
              className="flex min-w-0 flex-1 cursor-move touch-none select-none items-center gap-2"
              onPointerDown={startCardTeachBoxDrag}
              onPointerMove={moveCardTeachBox}
              onPointerUp={endCardTeachBoxDrag}
              onPointerCancel={endCardTeachBoxDrag}
              title={isCardTeachMode ? "Drag Teach me settings" : "Drag tutor settings"}
            >
              <GripHorizontal className="h-4 w-4 shrink-0 text-slate-400" strokeWidth={2.35} />
              <Settings className={cn("h-4 w-4 shrink-0", isDark ? "text-blue-300" : "text-blue-600")} />
              <div className="min-w-0">
                <h3 className={cn("truncate text-sm font-bold", isDark ? "text-white" : "text-slate-950")}>
                  {isCardTeachMode ? "Teach me settings" : "Tutor settings"}
                </h3>
                <p className={cn("truncate text-xs", isDark ? "text-slate-400" : "text-slate-500")}>
                  {teachSettingsInfo?.tutor?.full_name || activeTutor.full_name} • {teachSettingsInfo?.tutor?.subject_name || activeTutor.subject_name}
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <button
                type="button"
                onClick={minimizeCardTeachBox}
                className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-full border transition-colors",
                  isDark
                    ? "border-slate-700 bg-slate-900/80 text-slate-300 hover:bg-slate-800 hover:text-white"
                    : "border-slate-200 bg-white/85 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                )}
                title="Minimize call"
                aria-label="Minimize call"
              >
                <Minimize2 className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={closeTeachSettings}
                className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-full border transition-colors",
                  isDark
                    ? "border-slate-700 bg-slate-900/80 text-slate-300 hover:bg-slate-800 hover:text-white"
                    : "border-slate-200 bg-white/85 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                )}
                title="Back to call"
                aria-label="Back to call"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-hidden px-3.5 py-3">
            {teachSettingsLoading ? (
              <div className={cn("flex h-full flex-col items-center justify-center gap-2 text-xs", isDark ? "text-slate-400" : "text-slate-500")}>
                <Loader2 className="h-5 w-5 animate-spin" />
                Loading settings...
              </div>
            ) : teachSettingsError ? (
              <div className={cn("rounded-2xl border px-3 py-3 text-xs", isDark ? "border-red-500/30 bg-red-950/40 text-red-200" : "border-red-200 bg-red-50 text-red-700")}>
                {teachSettingsError}
                <button
                  type="button"
                  onClick={loadTeachSettings}
                  className="mt-2 block font-bold underline"
                >
                  Try again
                </button>
              </div>
            ) : (
              <div className="flex h-full min-h-0 flex-col gap-3">
                <div className={cn("shrink-0 rounded-2xl border p-3", isDark ? "border-slate-700 bg-slate-900/70" : "border-slate-200 bg-slate-50")}>
                  <div className={cn("flex min-w-0 items-center gap-3", isDark ? "text-slate-300" : "text-slate-700")}>
                    <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full", isDark ? "bg-slate-800" : "bg-slate-200")}>
                      {isCardTeachMode ? (
                        <img src="/logo.png" alt="Budlee" className="h-full w-full object-contain p-1" />
                      ) : activeTutor.avatar_url ? (
                        <img src={activeTutor.avatar_url} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <span className="text-sm font-bold">{activeTutor.full_name[0]}</span>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-bold">{isCardTeachMode ? 'Budlee AI Tutor' : (teachSettingsInfo?.tutor?.full_name || activeTutor.full_name)}</div>
                      <div className={cn("truncate text-xs", isDark ? "text-slate-400" : "text-slate-500")}>
                        {isCardTeachMode ? `${context?.subject_name || context?.subjectName || activeTutor.subject_name || 'Budlee'} Tutor` : `${teachSettingsInfo?.tutor?.subject_name || activeTutor.subject_name} Tutor`}
                      </div>
                    </div>
                  </div>
                </div>

                <div className={cn("flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border p-3", isDark ? "border-slate-700 bg-slate-900/70" : "border-slate-200 bg-slate-50")}>
                  <div className={cn("mb-2 flex shrink-0 items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider", isDark ? "text-slate-400" : "text-slate-500")}>
                    <Globe className="h-3.5 w-3.5" />
                    Tutor languages
                  </div>
                  {teachSettingsNotice && (
                    <div className={cn(
                      "mb-2 shrink-0 rounded-lg border px-2.5 py-2 text-[11px] font-semibold",
                      isDark
                        ? "border-amber-400/30 bg-amber-400/10 text-amber-100"
                        : "border-amber-200 bg-amber-50 text-amber-800"
                    )}>
                      {teachSettingsNotice}
                    </div>
                  )}
                  <div className="grid min-h-0 flex-1 grid-cols-1 gap-1.5 overflow-y-auto pr-1">
                    {LANGUAGES.map((language) => {
                      const isSelected = selectedTeachLanguages.includes(language.code);
                      const isPrimary = selectedTeachLanguages[0] === language.code;
                      return (
                        <button
                          key={language.code}
                          type="button"
                          onClick={() => toggleTeachLanguage(language.code)}
                          className={cn(
                            "flex min-w-0 items-center justify-between gap-2 rounded-xl border px-3 py-2 text-left text-xs font-semibold transition-colors",
                            isPrimary
                              ? "border-emerald-400 bg-emerald-500/15 text-emerald-700 dark:text-emerald-200"
                              : isSelected
                                ? "border-teal-400/70 bg-teal-500/10 text-teal-700 dark:text-teal-200"
                                : isDark
                                  ? "border-slate-700 bg-slate-950/50 text-slate-300 hover:border-emerald-500/50 hover:text-white"
                                  : "border-slate-200 bg-white text-slate-700 hover:border-emerald-300 hover:text-slate-950"
                          )}
                        >
                          <span className="min-w-0 truncate">{language.name}</span>
                          {isPrimary ? (
                            <span className="shrink-0 rounded-full bg-emerald-500 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wide text-white">Primary</span>
                          ) : isSelected ? (
                            <Check className="h-3.5 w-3.5 shrink-0" />
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className={cn("flex shrink-0 gap-2 border-t p-3", isDark ? "border-slate-800" : "border-slate-200")}>
            <Button
              type="button"
              variant="outline"
              onClick={closeTeachSettings}
              className="h-10 flex-1 rounded-xl text-xs"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={saveTeachLanguages}
              disabled={teachSettingsLoading || teachSettingsSaving || selectedTeachLanguages.length === 0}
              className="h-10 flex-1 rounded-xl bg-emerald-600 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {teachSettingsSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (cardTeachSurfaceMode === 'bubble') {
    const isSpeaking = realtime.state === 'speaking';
    const isUserSpeaking = realtime.state === 'listening';
    const isConnected = ['live', 'listening', 'speaking', 'consulting', 'thinking'].includes(realtime.state);
    const bubblePos = bubblePosition || getInitialCardTeachBoxPosition('bubble');

    return (
      <div
        className="fixed z-[250]"
        style={{
          left: `${bubblePos.x}px`,
          top: `${bubblePos.y}px`,
        }}
      >
        <motion.div
          role="button"
          tabIndex={0}
          aria-label="Teach me - Tap for shortcuts"
          title="Teach me • Tap for shortcuts"
          data-testid="card-teach-floating-bubble"
          animate={
            (isSpeaking || isUserSpeaking)
              ? { y: [0, -6, 0, -3, 0], scale: [1, 1.05, 1, 1.02, 1] }
              : { y: 0, scale: 1 }
          }
          transition={
            (isSpeaking || isUserSpeaking)
              ? { duration: 1.2, repeat: Infinity, ease: 'easeInOut' }
              : { duration: 0.2 }
          }
          className={cn(
            "relative flex h-12 w-12 cursor-pointer touch-none select-none items-center justify-center rounded-full border shadow-2xl backdrop-blur-xl transition-shadow active:scale-95",
            isConnected
              ? (isDark
                  ? "border-emerald-400/80 bg-slate-950/95 text-white shadow-[0_0_15px_rgba(16,185,129,0.35)]"
                  : "border-emerald-500/80 bg-white/95 text-slate-900 shadow-[0_0_15px_rgba(16,185,129,0.3)]")
              : (isDark
                  ? "border-blue-500/50 bg-slate-950/95 shadow-blue-900/30 text-white"
                  : "border-blue-300 bg-white/95 shadow-slate-900/20 text-slate-900")
          )}
          onPointerDown={(e) => {
            hasMovedRef.current = false;
            startCardTeachBoxDrag(e);
          }}
          onPointerMove={moveCardTeachBox}
          onPointerUp={endCardTeachBoxDrag}
          onPointerCancel={endCardTeachBoxDrag}
          onClick={() => {
            if (!hasMovedRef.current) {
              setShowBubbleShortcuts((prev) => !prev);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setShowBubbleShortcuts((prev) => !prev);
            }
          }}
        >
          {/* Connected breathing green ring */}
          {isConnected && !isSpeaking && !isUserSpeaking && (
            <span
              data-testid="card-teach-bubble-connected-ring"
              className="absolute -inset-1 rounded-full border-2 border-emerald-400/70 animate-pulse bg-emerald-500/10 pointer-events-none"
            />
          )}

          {/* Speaking ripples & aura */}
          {isSpeaking && (
            <>
              <span className="absolute inset-0 rounded-full animate-ping border-2 border-blue-400 opacity-60 pointer-events-none" />
              <span className="absolute -inset-1 rounded-full border border-blue-400/40 animate-pulse pointer-events-none" />
            </>
          )}

          {/* Listening ripples & aura */}
          {isUserSpeaking && (
            <>
              <span className="absolute inset-0 rounded-full animate-ping border-2 border-amber-400 opacity-60 pointer-events-none" />
              <span className="absolute -inset-1 rounded-full border border-amber-400/40 animate-pulse pointer-events-none" />
            </>
          )}

          <div className="relative flex h-8 w-8 items-center justify-center overflow-hidden rounded-full p-0.5">
            <img
              src="/logo.png"
              alt="Budlee"
              className="h-full w-full object-contain pointer-events-none select-none"
              data-testid="card-teach-bubble-avatar"
            />
          </div>

          {/* Directional expand badge indicating tap for controls */}
          <span
            data-testid="card-teach-bubble-expand-badge"
            title="Shortcuts"
            className={cn(
              "absolute -top-1 -left-1 flex h-4 w-4 items-center justify-center rounded-full border shadow-sm pointer-events-none transition-transform",
              isConnected
                ? "border-emerald-400 bg-emerald-600 text-white"
                : isDark
                  ? "border-blue-400 bg-blue-600 text-white"
                  : "border-blue-300 bg-blue-500 text-white"
            )}
          >
            <ArrowUpLeft className="h-2.5 w-2.5" />
          </span>

          {realtime.isMuted && (
            <span
              data-testid="card-teach-bubble-muted-dot"
              title="Microphone muted"
              className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-red-600 border border-white text-white shadow-sm pointer-events-none"
            >
              <MicOff className="h-2 w-2" />
            </span>
          )}
        </motion.div>

        {/* Quick Shortcuts Pop-up */}
        {showBubbleShortcuts && (
          <motion.div
            initial={{ opacity: 0, scale: 0.85, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            data-testid="card-teach-bubble-shortcuts"
            className={cn(
              "absolute z-50 flex items-center gap-2 rounded-2xl border px-3 py-2 shadow-2xl backdrop-blur-xl whitespace-nowrap",
              bubblePos.y > 110 ? "bottom-[calc(100%+10px)]" : "top-[calc(100%+10px)]",
              bubblePos.x > 180 ? "right-0" : "left-0",
              isDark
                ? "border-slate-700/80 bg-slate-950/95 text-white shadow-blue-950/50"
                : "border-slate-200 bg-white/95 text-slate-900 shadow-slate-900/15"
            )}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                realtime.toggleMute();
              }}
              className={cn(
                "flex h-9 w-9 items-center justify-center rounded-full border transition-all active:scale-95",
                realtime.isMuted
                  ? (isDark ? "bg-red-500/20 border-red-500 text-red-300" : "bg-red-50 border-red-300 text-red-600")
                  : (isDark ? "bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700" : "bg-slate-100 border-slate-300 text-slate-700 hover:bg-slate-200")
              )}
              title={realtime.isMuted ? "Unmute microphone" : "Mute microphone"}
              aria-label={realtime.isMuted ? "Unmute microphone" : "Mute microphone"}
              data-testid="bubble-shortcut-mic-btn"
            >
              {realtime.isMuted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            </button>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowBubbleShortcuts(false);
                close();
              }}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-red-600 text-white shadow-md shadow-red-600/30 hover:bg-red-700 active:scale-95 transition-all"
              title="End call"
              aria-label="End call"
              data-testid="bubble-shortcut-end-call-btn"
            >
              <PhoneOff className="h-4 w-4" />
            </button>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowBubbleShortcuts(false);
                expandFromBubble();
              }}
              className={cn(
                "flex h-9 w-9 items-center justify-center rounded-full border transition-all active:scale-95",
                isDark
                  ? "border-blue-500/40 bg-blue-950/50 text-blue-300 hover:bg-blue-900/60"
                  : "border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100"
              )}
              title="Maximize widget"
              aria-label="Maximize widget"
              data-testid="bubble-shortcut-maximize-btn"
            >
              <Maximize2 className="h-4 w-4" />
            </button>
          </motion.div>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        overlayChromeClass,
        overlayToneClass,
        isCompactMode && "cursor-move touch-none select-none"
      )}
      style={cardTeachBoxStyle}
      data-testid={isCardTeachMode ? 'card-teach-voice-panel' : 'voice-assistant-overlay'}
      onPointerDown={isCompactMode ? startCardTeachBoxDrag : undefined}
      onPointerMove={isCompactMode ? moveCardTeachBox : undefined}
      onPointerUp={isCompactMode ? endCardTeachBoxDrag : undefined}
      onPointerCancel={isCompactMode ? endCardTeachBoxDrag : undefined}
    >
      {/* Blurred background picture of tutor */}
      {activeTutor.avatar_url && !isCardTeachMode ? (
        <div
          className={cn(
            "absolute inset-0 w-full h-full bg-cover bg-center filter blur-3xl scale-105 pointer-events-none transition-all duration-1000",
            isDark ? "opacity-20" : "opacity-10"
          )}
          style={{ backgroundImage: `url(${activeTutor.avatar_url})` }}
        />
      ) : (
        <div className={cn(
          "absolute inset-0 w-full h-full pointer-events-none",
          isDark
            ? "bg-gradient-to-tr from-slate-900 via-blue-950 to-slate-900 opacity-60"
            : "bg-gradient-to-tr from-slate-100 via-blue-50 to-slate-100 opacity-80"
        )} />
      )}



      {/* Split Layout Container */}
      <div className={cn(
        "w-full h-full overflow-hidden relative z-10",
        isCompactMode ? "flex flex-col" : "flex flex-row"
      )}>

        {/* Left Section - Call Screen */}
        <div className={cn(
          "relative flex-1 flex flex-col items-center justify-center h-full",
          isCompactMode && "min-h-0 px-4 py-14"
        )}>
          {/* Top Left Controls */}
          <div className={cn(
            "absolute z-30 flex items-center",
            isCompactMode ? "left-3 top-3" : "left-4 top-4 sm:left-6 sm:top-6"
          )}>
            <button
              type="button"
              onClick={() => openTeachSettings(false)}
              className={cn(
                "flex items-center justify-center rounded-full border transition-colors cursor-pointer",
                isCompactMode ? "h-8 w-8" : "h-9 w-9 sm:h-10 sm:w-10",
                isDark
                  ? "border-slate-700 bg-slate-900/80 text-slate-300 hover:bg-slate-800 hover:text-white"
                  : "border-slate-200 bg-white/85 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
              )}
              title={isCardTeachMode ? "Teach me settings" : "Tutor settings"}
              aria-label={isCardTeachMode ? "Teach me settings" : "Tutor settings"}
            >
              <Settings className={cn(isCompactMode ? "h-4 w-4" : "h-4 w-4 sm:h-5 sm:w-5")} />
            </button>
          </div>

          {/* Top Banner (Header) */}
          <div className={cn(
            "absolute left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 whitespace-nowrap z-20 pointer-events-none",
            isCompactMode ? "top-3 max-w-[calc(100%-5.5rem)] px-1" : "top-6 lg:top-10"
          )}
            title={isCompactMode ? (isCardTeachMode ? "Drag Teach me" : "Drag AI Tutor") : undefined}
          >
            <div className={cn(
              "flex items-center gap-2 max-w-full overflow-hidden",
              isDark ? "text-white/60" : "text-slate-600"
            )}>
              {isCompactMode && <GripHorizontal className="h-4 w-4 shrink-0 text-slate-400" strokeWidth={2.35} />}
              <Headphones className="h-4 w-4 shrink-0 animate-pulse text-blue-400" />
              <span className={cn("text-xs uppercase tracking-widest font-semibold truncate", isDark ? "text-blue-300" : "text-blue-600")}>
                {isCardTeachMode ? 'Teach me' : (activeTutor.full_name || 'Call AI Tutor')}
              </span>
            </div>
            {(context?.subject_name || context?.subjectName) && (
              <span className={cn(
                "inline-flex max-w-full items-center overflow-hidden px-2.5 py-0.5 rounded-full text-[11px] font-bold border",
                isDark
                  ? "bg-emerald-950/40 border-emerald-500/20 text-emerald-400"
                  : "bg-emerald-50 border-emerald-200 text-emerald-700"
              )}>
                <span className="min-w-0 truncate">{context?.subject_name || context?.subjectName}</span>
                {(context?.chapter_name || context?.chapterName) && (
                  <>
                    <span className="mx-1 shrink-0 opacity-60">•</span>
                    <span className="min-w-0 truncate">{context?.chapter_name || context?.chapterName}</span>
                  </>
                )}
              </span>
            )}
            {showRipples && remainingSecondsLeft <= 60 && remainingSecondsLeft > 0 && (
              <span className="text-[11px] font-bold text-red-500 animate-pulse tracking-wide mt-1">
                Call will be disconnected in {remainingSecondsLeft} seconds
              </span>
            )}
          </div>

          {/* Top Right Controls */}
          <div className={cn(
            "absolute z-30 flex items-center gap-1.5",
            isCompactMode ? "right-3 top-3" : "right-4 top-4 sm:right-6 sm:top-6"
          )}>
            <button
              type="button"
              onClick={minimizeCardTeachBox}
              className={cn(
                "flex items-center justify-center rounded-full border transition-colors cursor-pointer",
                isCompactMode ? "h-8 w-8" : "h-9 w-9 sm:h-10 sm:w-10",
                isDark
                  ? "border-slate-700 bg-slate-900/80 text-slate-300 hover:bg-slate-800 hover:text-white"
                  : "border-slate-200 bg-white/85 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
              )}
              aria-label="Minimize"
              data-testid="minimize-voice-panel-btn"
            >
              <Minus className={cn(isCompactMode ? "h-4 w-4" : "h-4 w-4 sm:h-5 sm:w-5")} strokeWidth={2.5} />
            </button>
            {!isCardTeachMode && (
              <button
                type="button"
                onClick={() => setIsFullScreen(!isFullScreen)}
                className={cn(
                  "flex items-center justify-center rounded-full border transition-colors cursor-pointer",
                  isCompactMode ? "h-8 w-8" : "h-9 w-9 sm:h-10 sm:w-10",
                  isDark
                    ? "border-slate-700 bg-slate-900/80 text-slate-300 hover:bg-slate-800 hover:text-white"
                    : "border-slate-200 bg-white/85 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                )}
                title={isFullScreen ? "Exit full screen" : "Expand to full screen"}
                aria-label={isFullScreen ? "Exit full screen" : "Expand to full screen"}
              >
                {isFullScreen ? (
                  <Minimize2 className={cn(isCompactMode ? "h-4 w-4" : "h-4 w-4 sm:h-5 sm:w-5")} />
                ) : (
                  <Maximize2 className={cn(isCompactMode ? "h-4 w-4" : "h-4 w-4 sm:h-5 sm:w-5")} />
                )}
              </button>
            )}
          </div>

          {/* Call Duration / Status */}
          <div className={cn("text-center z-10", isCompactMode ? "mb-2" : "mb-8")}>
            <p className={cn(isCompactMode ? "text-xl" : "text-3xl", "font-mono tracking-wider font-bold", isDark ? "text-white" : "text-slate-900")}>
              {showRipples ? formatTime(durationSeconds) : '00:00'}
            </p>
            <p className={cn(
              "text-xs font-semibold uppercase tracking-widest mt-1.5 animate-pulse",
              isSpeaking ? (isDark ? "text-blue-400" : "text-blue-600") :
                isUserSpeaking ? (isDark ? "text-yellow-400" : "text-yellow-600") :
                  isThinking ? (isDark ? "text-purple-400" : "text-purple-600") :
                    (isDark ? "text-emerald-400" : "text-emerald-600")
            )}>
              {labelForState(realtime.state, studentFullName, activeTutor.full_name || 'Tutor')}
            </p>
          </div>

          {/* Center tutor avatar or synchronized visual lesson card */}
          <div className={cn(
            "relative flex items-center justify-center z-10",
            hasActiveVisual
              ? (isCompactMode ? "h-48 w-full max-w-[392px]" : "h-[min(48vh,440px)] w-[min(72vw,760px)]")
              : (isCompactMode ? "h-28 w-28 sm:h-36 sm:w-36" : "h-72 w-72 sm:h-80 sm:w-80")
          )}>
            {hasActiveVisual ? (
              <div className={cn(
                "relative h-full w-full overflow-hidden rounded-2xl border shadow-xl transition-all duration-500",
                isSpeaking ? "border-blue-400 shadow-blue-500/20" : "border-emerald-400/70 shadow-emerald-500/15",
                isDark ? "bg-slate-900" : "bg-white"
              )} data-testid="voice-visual-card">
                {activeVisualImage ? (
                  <img
                    src={activeVisualImage}
                    alt={activeVisualCard?.caption || activeVisualCard?.title || 'Lesson visual'}
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <div className={cn(
                    "flex h-full w-full items-center justify-center px-5 text-center text-sm font-bold",
                    isDark ? "text-slate-200" : "text-slate-700"
                  )}>
                    {activeVisualCard?.teaching_points?.[0] || activeVisualCard?.context || activeVisualCard?.title}
                  </div>
                )}
                <div className="absolute left-2 top-2 rounded-full bg-slate-950/80 px-2.5 py-1 text-[10px] font-black uppercase tracking-wide text-white backdrop-blur">
                  Card {activeVisualIndex + 1} of {visualCards.length}
                </div>
                {visualHighlight && (
                  <div className="absolute bottom-2 left-1/2 max-w-[90%] -translate-x-1/2 rounded-full border border-amber-300 bg-amber-400/95 px-3 py-1.5 text-center text-[11px] font-black text-amber-950 shadow-lg animate-pulse">
                    Look here: {visualHighlight}
                  </div>
                )}
              </div>
            ) : (
              <>
                {showRipples && (
                  <>
                    <span className={cn(
                      isCompactMode ? "absolute h-16 w-16 rounded-full border-2 sm:h-20 sm:w-20 animate-ping transition-all duration-500" : "absolute h-32 w-32 rounded-full border-2 sm:h-40 sm:w-40 animate-ping transition-all duration-500",
                      isSpeaking ? 'border-blue-400/40 bg-blue-500/5' :
                        isUserSpeaking ? 'border-yellow-400/40 bg-yellow-500/5' :
                          isThinking ? 'border-purple-400/40 bg-purple-500/5' :
                            'border-green-400/30 bg-green-500/2'
                    )} />
                    <span className={cn(
                      isCompactMode ? "absolute h-24 w-24 rounded-full border-2 sm:h-28 sm:w-28 animate-ping [animation-delay:260ms] transition-all duration-500" : "absolute h-44 w-44 rounded-full border-2 sm:h-56 sm:w-56 animate-ping [animation-delay:260ms] transition-all duration-500",
                      isSpeaking ? 'border-blue-400/30 bg-blue-500/3' :
                        isUserSpeaking ? 'border-yellow-400/30 bg-yellow-500/3' :
                          isThinking ? 'border-purple-400/30 bg-purple-500/3' :
                            'border-green-400/20 bg-green-500/1'
                    )} />
                    <span className={cn(
                      isCompactMode ? "absolute h-28 w-28 rounded-full border-2 sm:h-36 sm:w-36 animate-ping [animation-delay:520ms] transition-all duration-500" : "absolute h-56 w-56 rounded-full border-2 sm:h-72 sm:w-72 animate-ping [animation-delay:520ms] transition-all duration-500",
                      isSpeaking ? 'border-blue-400/20 bg-blue-500/1' :
                        isUserSpeaking ? 'border-yellow-400/20 bg-yellow-500/1' :
                          isThinking ? 'border-purple-400/20 bg-purple-500/1' :
                            'border-green-400/10 bg-green-500/0.5'
                    )} />
                  </>
                )}

                {isConnecting && (
                  <div className="absolute inset-0 rounded-full border border-green-500/20 animate-spin" style={{ animationDuration: '6s' }} />
                )}
                <div className={cn(
                  isCompactMode ? "absolute h-16 w-16 rounded-full border-4 transition-all duration-500 sm:h-20 sm:w-20 animate-spin z-0" : "absolute h-32 w-32 rounded-full border-8 transition-all duration-500 sm:h-40 sm:w-40 animate-spin z-0",
                  isSpeaking ? 'border-t-blue-500 border-b-blue-500 border-l-blue-500/30 border-r-blue-500/30 shadow-[0_0_30px_rgba(59,130,246,0.6)]' :
                    isUserSpeaking ? 'border-t-yellow-500 border-b-yellow-500 border-l-yellow-500/30 border-r-yellow-500/30 shadow-[0_0_30px_rgba(234,179,8,0.6)]' :
                      isThinking ? 'border-t-purple-500 border-b-purple-500 border-l-purple-500/30 border-r-purple-500/30 shadow-[0_0_30px_rgba(168,85,247,0.6)]' :
                        'border-t-green-500 border-b-green-500 border-l-green-500/30 border-r-green-500/30 shadow-[0_0_30px_rgba(34,197,94,0.4)]'
                )} style={{ animationDuration: isThinking ? '2s' : '4s' }} />
                <div className={cn(
                  isCompactMode ? "relative flex h-14 w-14 items-center justify-center rounded-full overflow-hidden sm:h-[72px] sm:w-[72px] z-10 pointer-events-none select-none" : "relative flex h-[112px] w-[112px] items-center justify-center rounded-full overflow-hidden sm:h-[144px] sm:w-[144px] z-10 pointer-events-none select-none",
                  isDark ? "bg-slate-800" : "bg-slate-200"
                )}>
                  {isCardTeachMode ? (
                    <img src="/logo.png" alt="Budlee AI Tutor" className="w-full h-full object-contain p-1.5 pointer-events-none select-none" draggable={false} data-testid="teach-me-standard-avatar" />
                  ) : activeTutor.avatar_url ? (
                    <img src={activeTutor.avatar_url} alt="" className="w-full h-full object-cover pointer-events-none select-none" draggable={false} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-3xl font-bold bg-blue-600 text-white select-none pointer-events-none">
                      {activeTutor.full_name[0]}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Tutor Info */}
          <div className={cn("text-center px-6 max-w-sm z-10", isCompactMode ? "mt-2" : "mt-8")}>
            <h2 className={cn(isCompactMode ? "text-base" : "text-xl", "font-bold tracking-wide text-center", isDark ? "text-white" : "text-slate-900")}>
              {isCardTeachMode ? 'Budlee AI Tutor' : activeTutor.full_name}
              <span className={cn(
                "inline-block text-[9px] font-bold border px-1.5 py-0.2 rounded-full align-super ml-1.5",
                isDark ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10" : "text-emerald-700 border-emerald-300 bg-emerald-50"
              )}>AI</span>
            </h2>
            <p className={cn("text-xs font-semibold mt-1", isDark ? "text-slate-400" : "text-slate-500")}>
              {isCardTeachMode ? `${context?.subject_name || context?.subjectName || activeTutor.subject_name || 'Budlee'} Tutor` : `${activeTutor.subject_name} Tutor`}
            </p>
            {(activeVisualCard?.title || cardTitle) && (
              <p className={cn("mt-2 line-clamp-2 text-xs font-semibold", isDark ? "text-slate-300" : "text-slate-600")}>
                {activeVisualCard?.title || cardTitle}
              </p>
            )}
          </div>

          {realtime.error && (
            <div className="mt-4 w-full max-w-[440px] px-4 z-10">
              <div className={cn(
                "rounded-2xl border px-4 py-3 text-xs text-center",
                isDark ? "border-red-500/25 bg-red-950/50 text-red-200" : "border-red-200 bg-red-50 text-red-700"
              )}>
                {realtime.error}
              </div>
            </div>
          )}

          {/* Action Buttons (Footer Controls) */}
          <div className={cn(
            "absolute left-0 right-0 flex items-center justify-center px-4 z-20",
            isCompactMode ? "bottom-3 gap-3" : "bottom-4 gap-6"
          )}>
            {/* Mute Button */}
            <Button
              type="button"
              onClick={realtime.toggleMute}
              className={cn(
                "cursor-pointer",
                isCompactMode ? "rounded-full w-10 h-10 flex items-center justify-center border transition-all duration-300 p-0" : "rounded-full w-12 h-12 flex items-center justify-center border transition-all duration-300 p-0",
                realtime.isMuted
                  ? (isDark ? "bg-red-500/20 border-red-500 text-red-400 hover:bg-red-500/30" : "bg-red-50 border-red-300 text-red-600 hover:bg-red-100")
                  : (isDark ? "bg-slate-800/80 border-slate-700 text-slate-300 hover:bg-slate-700" : "bg-white border-slate-300 text-slate-700 hover:bg-slate-100")
              )}
              title={realtime.isMuted ? "Unmute Microphone" : "Mute Microphone"}
            >
              {realtime.isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />}
            </Button>

            {/* Captions Toggle Button */}
            <Button
              type="button"
              onClick={() => setIsTranscriptExpanded(!isTranscriptExpanded)}
              className={cn(
                "cursor-pointer",
                isCompactMode ? "rounded-full w-10 h-10 flex items-center justify-center border transition-all duration-300 p-0" : "rounded-full w-12 h-12 flex items-center justify-center border transition-all duration-300 p-0",
                isTranscriptExpanded
                  ? (isDark ? "bg-blue-500/20 border-blue-500 text-blue-400 hover:bg-blue-500/30" : "bg-blue-50 border-blue-300 text-blue-600 hover:bg-blue-100")
                  : (isDark ? "bg-slate-800/80 border-slate-700 text-slate-300 hover:bg-slate-700" : "bg-white border-slate-300 text-slate-700 hover:bg-slate-100")
              )}
              title={isTranscriptExpanded ? "Hide Captions" : "Show Captions"}
            >
              <span className="text-[10px] font-black tracking-wider leading-none px-1 py-0.5 border border-current rounded-sm">
                CC
              </span>
            </Button>

            {/* End Call Button */}
            <Button
              type="button"
              onClick={close}
              className={cn(
                "cursor-pointer rounded-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center shadow-lg shadow-red-600/30 transition-all hover:scale-105 active:scale-95 p-0",
                isCompactMode ? "h-12 w-12" : "w-14 h-14"
              )}
              title="End Call"
            >
              <PhoneOff className="h-6 w-6" />
            </Button>

            {/* Retry Button (if error) */}
            {realtime.state === 'error' && (
              <Button
                type="button"
                onClick={() => realtime.start()}
                className={cn(
                  "cursor-pointer",
                  isCompactMode ? "rounded-full w-10 h-10 flex items-center justify-center p-0 transition-all hover:scale-105" : "rounded-full w-12 h-12 flex items-center justify-center p-0 transition-all hover:scale-105",
                  isDark ? "bg-white text-slate-950 hover:bg-slate-100" : "bg-slate-900 text-white hover:bg-slate-850"
                )}
                title="Try Again"
              >
                <RotateCcw className="h-5 w-5" />
              </Button>
            )}
          </div>
        </div>

        {/* Right Section - Transcript/Captions Sidebar */}
        <div className={cn(
          "transition-all duration-500 ease-in-out flex flex-col overflow-hidden backdrop-blur-md z-30 shrink-0",
          isCompactMode
            ? "absolute inset-0 w-full"
            : "absolute inset-y-0 right-0 w-full lg:relative lg:inset-auto lg:right-auto lg:h-full lg:border-l",
          isDark
            ? (isCompactMode ? "border-white/5 bg-slate-950/95" : "border-white/5 bg-slate-950/95 lg:bg-slate-900/30")
            : (isCompactMode ? "border-slate-200 bg-white/95" : "border-slate-200 bg-white lg:bg-slate-50/50"),
          isTranscriptExpanded
            ? (isCompactMode ? "translate-x-0 opacity-100" : "translate-x-0 opacity-100 lg:w-[380px]")
            : (isCompactMode ? "translate-x-full opacity-0 pointer-events-none" : "translate-x-full opacity-0 pointer-events-none lg:w-0 lg:translate-x-0 lg:opacity-0")
        )}
          data-no-drag="true"
        >
          <div className="w-full h-full flex flex-col shrink-0">
            {/* Header */}
            <div className={cn("p-4 border-b flex items-center justify-between shrink-0", isDark ? "border-white/5" : "border-slate-200")}>
              <span className={cn("text-xs uppercase tracking-widest font-bold", isDark ? "text-blue-300" : "text-blue-600")}>
                Transcript &bull; With {activeTutor.full_name}
              </span>
              <button
                onClick={() => setIsTranscriptExpanded(false)}
                className={cn(
                  "p-1 rounded-full transition-colors",
                  isDark ? "hover:bg-white/5 text-slate-400 hover:text-white" : "hover:bg-slate-100 text-slate-500 hover:text-slate-800"
                )}
                title="Close Transcript"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages list */}
            <div className="flex-1 overflow-y-auto p-4 space-y-1.5 min-h-0 scrollbar-thin">
              {realtime.messages.length === 0 ? (
                <div className={cn("text-center py-10 text-xs italic", isDark ? "text-slate-500" : "text-slate-400")}>
                  Conversation transcript will appear here...
                </div>
              ) : (
                realtime.messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={cn(
                      "flex w-full",
                      msg.sender === 'user' ? "justify-end" : "justify-start"
                    )}
                  >
                    <div
                      className={cn(
                        "max-w-[85%] rounded-2xl px-3 py-1.5 text-xs transition-all duration-300 shadow-sm leading-relaxed",
                        msg.sender === 'user'
                          ? (isDark ? "bg-emerald-950/60 border border-emerald-500/20 text-emerald-100" : "bg-emerald-50 border border-emerald-200 text-emerald-800")
                          : (isDark ? "bg-slate-900/80 border border-slate-700/30 text-slate-100" : "bg-slate-100 border border-slate-200 text-slate-900")
                      )}
                    >
                      <p className="whitespace-pre-wrap">{msg.text}</p>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>
        </div>

      </div>

      {/* Resize Handle (bottom-right corner) */}
      {isCompactMode && !isCardTeachBubble && (
        <div
          data-testid="card-teach-resize-handle"
          className="absolute bottom-1 right-1 flex h-6 w-6 cursor-nwse-resize touch-none select-none items-end justify-end p-1 text-slate-400 hover:text-blue-500 z-40 transition-colors"
          onPointerDown={startCardTeachResize}
          onPointerMove={moveCardTeachResize}
          onPointerUp={endCardTeachResize}
          onPointerCancel={endCardTeachResize}
          title="Drag to resize"
          aria-label="Resize widget"
        >
          <svg width="10" height="10" viewBox="0 0 10 10" className="fill-current opacity-60 hover:opacity-100">
            <circle cx="8" cy="8" r="1.2" />
            <circle cx="8" cy="4" r="1.2" />
            <circle cx="4" cy="8" r="1.2" />
          </svg>
        </div>
      )}
    </div>
  );
});

export default RealtimeVoiceAssistantOverlay;
